import React, { useState } from "react";
import axios from "axios";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import {
  Lock,
  Loader2,
  CheckCircle2,
  KeyRound,
  ArrowLeft,
  ShieldCheck,
  AlertTriangle,
  Eye,
  EyeOff,
} from "lucide-react";
import { HOSPITAL } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPassword = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const canSubmit = pw.length >= 8 && pw === pw2 && !!token;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 8) {
      setError("Password minimal 8 karakter");
      return;
    }
    if (pw !== pw2) {
      setError("Konfirmasi password tidak sama");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: pw,
      });
      setDone(true);
      setTimeout(() => navigate("/admin/login"), 2500);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Gagal memperbarui password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-100 via-blue-50 to-slate-100 p-6">
      <div
        data-testid="reset-password-page"
        className="w-full max-w-md bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden"
      >
        <div className="bg-gradient-to-r from-blue-900 to-slate-900 p-6 text-white text-center">
          <img
            src={HOSPITAL.logo}
            alt="Logo"
            className="h-14 w-14 rounded-2xl object-cover mx-auto mb-3 ring-2 ring-white/10"
          />
          <div className="font-heading font-bold text-lg">RS Bhayangkara Tarakan</div>
          <div className="text-xs text-blue-200/80 mt-1">Buat Password Baru</div>
        </div>

        <div className="p-8">
          {!token && (
            <div
              data-testid="reset-password-invalid-token"
              className="p-4 rounded-2xl bg-amber-50 border border-amber-200 text-amber-800"
            >
              <div className="flex items-center gap-2 font-heading font-semibold mb-1">
                <AlertTriangle className="h-4 w-4" />
                Tautan Tidak Valid
              </div>
              <div className="text-sm">
                Token reset tidak ditemukan di URL. Silakan minta ulang tautan
                reset password.
              </div>
              <Link
                to="/admin/forgot-password"
                className="inline-flex items-center gap-1 mt-3 text-xs text-blue-700 hover:underline"
              >
                <ArrowLeft className="h-3 w-3" /> Minta ulang tautan
              </Link>
            </div>
          )}

          {token && !done && (
            <form onSubmit={handleSubmit}>
              <h1 className="font-heading text-xl font-bold text-slate-900 mb-2">
                Buat Password Baru
              </h1>
              <p className="text-sm text-slate-500 mb-6 leading-relaxed">
                Minimal 8 karakter. Gunakan kombinasi huruf dan angka untuk
                keamanan lebih baik.
              </p>

              <label className="block text-sm font-medium text-slate-700 mb-2">
                <Lock className="inline h-4 w-4 mr-1 text-blue-700" />
                Password Baru
              </label>
              <div className="relative mb-4">
                <input
                  type={showPw ? "text" : "password"}
                  required
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  data-testid="reset-password-input-new"
                  placeholder="Minimal 8 karakter"
                  className="w-full pl-4 pr-11 py-3 rounded-xl border border-slate-200 focus:border-blue-800 focus:ring-2 focus:ring-blue-100 outline-none text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  data-testid="reset-password-toggle-visibility"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                >
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>

              <label className="block text-sm font-medium text-slate-700 mb-2">
                <Lock className="inline h-4 w-4 mr-1 text-blue-700" />
                Konfirmasi Password
              </label>
              <input
                type={showPw ? "text" : "password"}
                required
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                data-testid="reset-password-input-confirm"
                placeholder="Ulangi password"
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-800 focus:ring-2 focus:ring-blue-100 outline-none text-sm mb-4"
              />

              {pw && pw2 && pw !== pw2 && (
                <div className="mb-3 text-xs text-red-600 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  Password dan konfirmasi tidak sama
                </div>
              )}

              {error && (
                <div
                  data-testid="reset-password-error"
                  className="mb-4 p-3 rounded-xl bg-red-50 border border-red-100 text-xs text-red-700"
                >
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={!canSubmit || loading}
                data-testid="reset-password-submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-blue-800 hover:bg-blue-900 disabled:opacity-60 disabled:cursor-not-allowed text-white font-medium px-6 py-3 rounded-full transition shadow-md shadow-blue-900/20"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <KeyRound className="h-4 w-4" />
                )}
                Simpan Password Baru
              </button>
            </form>
          )}

          {done && (
            <div data-testid="reset-password-success" className="text-center">
              <div className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="h-8 w-8 text-emerald-600" />
              </div>
              <h1 className="font-heading text-xl font-bold text-slate-900 mb-2">
                Password Berhasil Diperbarui
              </h1>
              <p className="text-sm text-slate-600 mb-4">
                Anda akan diarahkan ke halaman login dalam beberapa detik...
              </p>
              <Link
                to="/admin/login"
                className="inline-flex items-center gap-1 text-xs text-blue-800 hover:underline"
              >
                Login sekarang
              </Link>
            </div>
          )}

          <div className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-center gap-2 text-xs text-slate-400">
            <ShieldCheck className="h-3.5 w-3.5" />
            Password Anda dienkripsi dengan bcrypt sebelum disimpan.
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
